import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { ServicePlanCategory } from 'src/features/servicePlanCategory/utils/types';
import { useDeleteServicePlanCategory } from 'src/features/servicePlanCategory/api/useDeleteServicePlanCategory';
import {
    LIST_SERVICE_PLAN_CATEGORIES_RESOURCE
} from 'src/features/servicePlanCategory/api/useListServicePlanCategories';
import { servicePlanCategoriesMessages } from 'src/features/servicePlanCategory/utils/messages';

interface DeleteServicePlanCategoryFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanCategory: ServicePlanCategory;
    children?: React.ReactNode;
}

function DeleteServicePlanCategoryFormModal<TData>({
                                              selectedServicePlanCategory,
                                              children,
                                          }: DeleteServicePlanCategoryFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { mutate: deletePayStation } = useDeleteServicePlanCategory({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLAN_CATEGORIES_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(servicePlanCategoriesMessages.deleteServicePlanCategorySuccess),
                description: formatMessage(servicePlanCategoriesMessages.deleteServicePlanCategorySuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...servicePlanCategoriesMessages.deleteServicePlanCategory },
                subTitle: {
                    ...servicePlanCategoriesMessages.deleteServicePlanCategorySubtitle,
                    values: {
                        entity: selectedServicePlanCategory.name
                    }
            },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...servicePlanCategoriesMessages.confirmDeleteServicePlanCategory},
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: servicePlanCategoriesMessages.deleteServicePlanCategory,
                callbackFn: () => {
                    return (
                        deletePayStation({
                            id: selectedServicePlanCategory.id,
                        }),
                            setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteServicePlanCategoryFormModal;
